/** Single rack-name formatter. Matches backend format_rack_name (+1 display). */

export function operatorPositionToIndex(position: number): number {
  if (!Number.isInteger(position) || position < 1) {
    throw new RangeError('Operator position must be a positive integer')
  }
  return position - 1
}

export function rackAddressFromOperatorPositions(row: number, rack: number): {
  rowIndex: number
  rackIndex: number
} {
  return {
    rowIndex: operatorPositionToIndex(row),
    rackIndex: operatorPositionToIndex(rack),
  }
}

export function rackMembershipUpdate(
  rowId: string | undefined,
  rowLabelIndex: number | undefined,
  rackPosition: number | undefined,
): {
  rowId: string | undefined
  rackPosInRow: number | undefined
  rowIndex?: number
  rackIndex?: number
} {
  if (!rowId || rowLabelIndex === undefined) {
    return { rowId: undefined, rackPosInRow: undefined }
  }
  const position = rackPosition || 1
  return {
    rowId,
    rackPosInRow: position,
    ...rackAddressFromOperatorPositions(rowLabelIndex, position),
  }
}

export function rowIndexToLetter(index: number): string {
  let result = ''
  let n = index
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result
    n = Math.floor(n / 26) - 1
  }
  return result
}

export function formatRackName(
  template: string | undefined,
  prefix: string | undefined,
  rowIdx: number,
  rackIdx: number,
): string {
  const pfx = prefix ?? ''
  let tmpl = template || '{prefix}/{row:02d}/{rack:02d}'
  tmpl = tmpl.replace(/\{row_letter(?::[^}]+)?\}/g, rowIndexToLetter(rowIdx))
  tmpl = tmpl.replace(/\{prefix(?::[^}]*)?\}/g, pfx)
  tmpl = tmpl.replace(/\{module(?::[^}]*)?\}/g, pfx)
  tmpl = tmpl.replace(/\{hall(?::[^}]*)?\}/g, pfx)
  tmpl = tmpl.replace(/\{row:0(\d+)d\}/g, (_, digits) =>
    String(rowIdx + 1).padStart(parseInt(digits, 10), '0'))
  tmpl = tmpl.replace(/\{rack:0(\d+)d\}/g, (_, digits) =>
    String(rackIdx + 1).padStart(parseInt(digits, 10), '0'))
  tmpl = tmpl.replace(/\{row\}/g, String(rowIdx + 1))
  tmpl = tmpl.replace(/\{rack\}/g, String(rackIdx + 1))
  return tmpl
}

export interface RackRowMembership {
  address_confirmed?: boolean
  row_id?: string | null
  module_id?: string | null
  datacenter_code?: string
  rack_pos_in_row?: number | null
}

export function confirmedRowRacks<T extends RackRowMembership>(racks: readonly T[], current: T | null): T[] {
  if (current?.address_confirmed !== true || !current.row_id) return []
  return racks.filter(r => r.address_confirmed === true
    && r.row_id === current.row_id
    && (r.module_id ?? null) === (current.module_id ?? null)
    && r.datacenter_code === current.datacenter_code
    && Number.isInteger(r.rack_pos_in_row) && (r.rack_pos_in_row ?? 0) > 0)
    .sort((a, b) => a.rack_pos_in_row! - b.rack_pos_in_row!)
}

/** Saved labels survive unconfirmed placement; legacy callers retain their formatter fallback. */
export function rackLabel(
  r: { label?: string | null; row_index: number; rack_index: number; address_confirmed?: boolean },
  dc?: { rack_naming_template?: string; prefix?: string },
): string {
  if (r.label) return r.label
  if (r.address_confirmed === false) return 'Метка не задана'
  return formatRackName(dc?.rack_naming_template, dc?.prefix, r.row_index, r.rack_index)
}
